import * as React from "react";
import * as ReactDOM from "react-dom";
import { Route } from "react-router-dom";
import { IComboBoxValue } from "../../../models/form";
import * as modelsGet from "../../../models/get";
import * as modelsPost from "../../../models/post";
import * as utils from "../../../utils/utils";
import * as apiRules from "../../../api/rules";
import * as apiCalendars from "../../../api/calendars";
import * as apiMourningHalls from "../../../api/mourningHalls";
import { Window } from "../../../components/window";
import { Loader, LoaderStyle } from "../../../components/loader";
import { Button, ButtonType } from "../../../components/button";
import { ScrollViewer, ScrollBarVisibility } from "../../../components/scrollViewer";
import { FormGroup } from "../../../components/form";
import { ComboBox, ComboBoxType, DROPDOWN_TEXT_ITEM_HEIGHT, DropDownTextItem } from "../../../components/comboBox";
import { InputMask, InputSize } from "../../../components/input";
import { InputValueType } from "../../../enumerations";
import { TextBox } from "../../../components/textBox";
import { Alert, AlertType } from "../../../components/alert";
import RulesEditor from "./rulesEditor";

enum Mode
{
    create,
    edit
}

interface IProperties
{
    /**
     * Die ID des zu öffnenden Datensatzes. (Ist leer wenn eine neuer Datensatz erstellt werden soll.)
     */
    ruleId?: number,

    /**
     * Wird aufgerufen sobald sich das Fenster schließt.
     */
    onClose?: (refresh?: boolean) => void
}

interface IState
{
    /**
     * Gibt an, ob Daten gerade geladen werden.
     */
    isLoading?: boolean,

    /**
     * Gibt den Modus an in dem sich der Dialog befindet.
     */
    mode?: Mode,

    /**
     * Wenn im Bearbeitungsmodus, dann ist diese Eigenschaft gefüllt.
     */
    rule?: modelsGet.IRuleMourningHall,

    /**
     * Enthält alle Eingerichteten Trauerhallen.
     */
    mourningHalls?: modelsGet.IMourningHall[],

    /**
     * Enthält alle eingerichteten Kalender.
     */
    calendars?: modelsGet.ICalendar[],

    /**
     * Gibt an, ob das "Dann" Snippet im RuleEditor sichtbar ist.
     */
    isThenVisible?: boolean,

    /**
     * Enthält die Eingabedaten des Forms.
     */
    form?: {
        name?: string,
        when?: IComboBoxValue<string, number>,
        calendars?: IComboBoxValue<string, number>[],
        then?: IComboBoxValue<string, number>,
    },

    /**
     * Enthält Serverseitige Fehlermeldungen.
     */
    errors?: Object,

    /**
     * Der Text der Meldungen welche angezeigt werden soll.
     */
    alertMessage?: string,

    /**
     * Der Typ der Meldung.
     */
    alertType?: AlertType
}

export default class RulesDialog extends React.Component<IProperties, IState>
{
    private _initialState: IState;
    private _isSaveButtonDisabled: boolean;

    /**
     * Erstellt eine neue Instanz der Klasse @see MourningHallsRulesDialog.
     * @param props Die Eigenschaften dieser Komponente.
     */
    constructor(props: IProperties)
    {
        super(props);

        // Modus ermitteln.
        var mode: Mode = Mode.create;
        if (props.ruleId > 0)
        {
            mode = Mode.edit;
        }

        this.state = {
            isLoading: true,
            mode: mode,
            form: {
                when: {},
                then: {},
                calendars: []
            },
            errors: {}
        };

        this.initAsync(props);
    }

    /**
     * Lädt alle benötigten Daten und/oder weißt diese zu.
     * @param props Alle gesetzten Eigenschaften.
     */
    private async initAsync(props: IProperties)
    {
        if (this.state.mode === Mode.edit)
        {
            await this.initEditModeAsync(props);
        }
        else if (this.state.mode === Mode.create)
        {
            await this.initCreateModeAsync(props);
        }
    }

    /**
     * Initialisert den Edit Mode.
     * @param props Alle gesetzten Eigenschaften.
     */
    private async initEditModeAsync(props: IProperties)
    {
        // Trauerhalle aus der Datenbank laden.
        var rule: modelsGet.IRuleMourningHall = await apiRules.getRuleForMourningHall(props.ruleId);

        // Alle eingerichteten Trauerhallen ermitteln.
        var mourningHalls = await apiMourningHalls.getMourningHalls();

        // Alle eingerichteten Kalender ermitteln.
        var calendars = await apiCalendars.getCalendars();

        // Werte setzen.
        this.setState((state: IState) =>
        {
            state.isLoading = false;
            state.errors = {};
            state.alertMessage = null;
            state.alertType = null;
            state.rule = rule;
            state.mourningHalls = mourningHalls;
            state.calendars = calendars;

            state.isThenVisible = true;
            state.form.name = rule.name;
            state.form.when = {
                value: rule.when_name,
                selectedValue: rule.when_id
            };
            state.form.then = {
                value: rule.then_name,
                selectedValue: rule.then_id
            };

            state.form.calendars = [];
            if (rule.calendars != null && rule.calendars.length > 0)
            {
                for (var i = 0; i < rule.calendars.length; i++)
                {
                    state.form.calendars.push({
                        value: rule.calendars[i].calendar_name,
                        selectedValue: rule.calendars[i].calendar_id
                    });
                }
            }

            return state;
        }, () => this._initialState = {
            ...this.state,
            form: {
                ...this.state.form,
                calendars: this.state.form.calendars.slice()
            }
        });
    }

    /**
     * Initialisert den Create Mode.
     * @param props Alle gesetzten Eigenschaften.
     */
    private async initCreateModeAsync(props: IProperties)
    {
        // Alle eingerichteten Trauerhallen ermitteln.
        var mourningHalls = await apiMourningHalls.getMourningHalls();

        // Alle eingerichteten Kalender ermitteln.
        var calendars = await apiCalendars.getCalendars();

        // Neues State Objekt erstellen.
        this.setState(() =>
        {
            const newState: IState = {};
            newState.isLoading = false;
            newState.errors = {};
            newState.alertMessage = null;
            newState.alertType = null;
            newState.mourningHalls = mourningHalls;
            newState.calendars = calendars;

            newState.form = {
                when: {},
                then: {},
                calendars: []
            };

            return newState;
        }, () => this._initialState = {
            ...this.state,
            form: {
                ...this.state.form
            }
        });
    }

    /**
     * Wird ausgeführt, bevor gerendert wird.
     * Hier wird geprüft ob der Button zum erstellen oder speichern
     * freigschaltet werden soll.
     * @param nextProps Die neuen Eigenschaften.
     * @param nextState Der neue State.
     */
    componentWillUpdate(nextProps: IProperties, nextState: IState)
    {
        this._isSaveButtonDisabled = true;

        if (this._initialState != null)
        {
            const { form } = this._initialState;

            // Name prüfen.
            if (form.name !== nextState.form.name)
                this._isSaveButtonDisabled = false;

            // Wenn prüfen.
            if (form.when.selectedValue !== nextState.form.when.selectedValue)
                this._isSaveButtonDisabled = false;

            // Prüfen ob sich die Kalender geändert haben.
            if (form.calendars.length !== nextState.form.calendars.length)
            {
                this._isSaveButtonDisabled = false;
            }
            else if ((form.calendars.length > 0 && nextState.form.calendars.length > 0) && (!form.calendars.any(l => nextState.form.calendars.any(l2 => l2.selectedValue === l.selectedValue))))
            {
                this._isSaveButtonDisabled = false;
            }

            // Dann prüfen.
            if (form.then.selectedValue !== nextState.form.then.selectedValue)
                this._isSaveButtonDisabled = false;
        }
    }

    /**
     * Wird ausgeführt, bevor der Dialog sich schließt.
     */
    private onClosing = (refresh: boolean = false) =>
    {
        var closeDialog: boolean = true;

        if (!this._isSaveButtonDisabled)
            closeDialog = confirm("Es sind noch ungespeicherte Änderungen vorhanden. Wollen Sie den Dialog trotzdem schließen?");

        if (closeDialog)
        {
            const { onClose } = this.props;
            onClose && onClose(refresh);
            return true;
        }
    }

    /**
     * Ändert den geöffneten Datensatz oder erstellt einen neuen.
     */
    private onSaveOrCreateClick = async () =>
    {
        try
        {
            this.setState({
                isLoading: true
            });

            const { mode, rule, form } = this.state;

            // Neues POST Objekt erstellen.
            var newData: modelsPost.IRuleMourningHall = {
                name: form.name,
                when_id: form.when.selectedValue,
                calendar_ids: form.calendars.where(c1 => c1.selectedValue != null).select(c2 => c2.selectedValue),
                then_id: form.then.selectedValue
            };

            if (mode === Mode.edit)
            {
                await apiRules.putRuleForMourningHall(rule.id, newData);
            }
            else
            {
                await apiRules.postRuleForMourningHall(newData);
            }

            // Fenster schließen.
            this._isSaveButtonDisabled = true;
            this.onClosing(true);
        }
        catch (e)
        {
            var errors = utils.getErrors(e);
            var alertMessage: string;

            if (errors != null)
            {
                for (let error in errors)
                {
                    switch (error)
                    {
                        case "error":
                        case "date_is_invalid":
                            alertMessage = errors[error];
                            break;
                        case "ThenId":
                            {
                                if (!this.state.isThenVisible)
                                    alertMessage = "Fehler beim speichern der Regel. Das \"Dann\" Snippet fehlt.";
                            }
                            break;
                        case "internal_server_error":
                            alertMessage = "Auf dem Server ist ein Fehler aufgetreten! Wenn der Fehler weiterhin auftritt, kontaktieren Sie Ihren Administrator oder Ihren Software Anbieter.";
                            break;
                    }
                }
            }

            this.setState(() =>
            {
                const newState: IState = {};
                newState.isLoading = false;
                newState.errors = errors;
                newState.alertMessage = alertMessage;
                newState.alertType = AlertType.error;
                return newState;
            });
        }
    }

    /**
     * Löscht den geöffneten Datensatz.
     */
    private onDeleteClick = async () =>
    {
        var deleteData = confirm("Wollen Sie den Datensatz wirklich löschen?");
        if (!deleteData)
            return;

        try
        {
            this.setState({
                isLoading: true
            });

            const { rule } = this.state;

            await apiRules.remove(rule.id);

            // Fenster schließen.
            this._isSaveButtonDisabled = true;
            this.onClosing(true);
        }
        catch (e)
        {
            var errors = utils.getErrors(e);
            var alertMessage: string;

            if (errors != null)
            {
                for (let error in errors)
                {
                    switch (error)
                    {
                        case "error":
                        case "date_is_invalid":
                            alertMessage = errors[error];
                            break;
                        case "internal_server_error":
                            alertMessage = "Auf dem Server ist ein Fehler aufgetreten! Wenn der Fehler weiterhin auftritt, kontaktieren Sie Ihren Administrator oder Ihren Software Anbieter.";
                            break;
                    }
                }
            }

            this.setState(() =>
            {
                const newState: IState = {};
                newState.isLoading = false;
                newState.errors = errors;
                newState.alertMessage = alertMessage;
                newState.alertType = AlertType.error;
                return newState;
            });
        }
    }

    /**
     * Wird ausgeführt, sobald sich der Wert eines Controls ändert.
     * @param value Der konvertierte Wert der TextBox.
     * @param originalValue Der originale Wert der TextBox.
     * @param name Der Name des Form Elements.
     */
    private onControlTextChanged = (value: any[], originalValue: string[], name: string) =>
    {
        this.setState((state: IState) =>
        {
            state.form[name] = value[0];
            return state;
        });
    }

    /**
     * Wird ausgeführt, sobald sich der Wert von "Wenn" ändert.
     * @param selectedItem Der ausgewählte Datensatz.
     */
    private onWhenChanged = (selectedItem: modelsGet.IMourningHall) =>
    {
        this.setState((state: IState) =>
        {
            state.form.when = {
                value: selectedItem ? selectedItem.name : null,
                selectedValue: selectedItem ? selectedItem.id : null
            };
            return state;
        });
    }

    /**
     * Wird ausgeführt, sobald sich der Wert von "Dann" ändert.
     * @param selectedItem Der ausgewählte Datensatz.
     */
    private onThenChanged = (selectedItem: modelsGet.IMourningHall) =>
    {
        this.setState((state: IState) =>
        {
            state.form.then = {
                value: selectedItem ? selectedItem.name : null,
                selectedValue: selectedItem ? selectedItem.id : null
            };
            return state;
        });
    }

    /**
     * Wird ausgeführt, sobald ein Kalender zur Regel hinzugefügt wird.
     * @param selectedItem Der ausgewählte Datensatz.
     */
    private onCalendarAdded = () =>
    {
        this.setState((state: IState) =>
        {
            state.form.calendars.push({});
            return state;
        });
    }

    /**
     * Wird ausgeführt, sobald ein Kalender von der Regel entfernt wird.
     * @param selectedItem Der ausgewählte Datensatz.
     */
    private onCalendarRemoved = (index: number) =>
    {
        this.setState((state: IState) =>
        {
            state.form.calendars.splice(index, 1);
            return state;
        });
    }

    /**
     * Wird ausgeführt, sobald sich der Wert von einer Kalender ComboBox ändert.
     * @param selectedItem Der ausgewählte Datensatz.
     */
    private onCalendarChanged = (selectedItem: modelsGet.ICalendar, index: number) =>
    {
        this.setState((state: IState) =>
        {
            state.form.calendars[index] = {
                value: selectedItem ? selectedItem.name : null,
                selectedValue: selectedItem ? selectedItem.id : null
            };
            return state;
        });
    }

    /**
     * Wird ausgeführt, sobald das Snippet "Dann" hinzugefügt wird.
     */
    private onThenAdded = () =>
    {
        this.setState({
            isThenVisible: true
        });
    }

    /**
     * Wird ausgeführt, sobald das Snippet "Dann" entfernt wird.
     */
    private onThenRemoved = () =>
    {
        this.setState({
            isThenVisible: false
        });
    }

    /**
     * Rendert das Formular/den Content Bereich.
     */
    private renderContent(): JSX.Element
    {
        const {
            mode,
            rule,
            mourningHalls,
            calendars,
            form,
            isThenVisible,
            errors
        } = this.state;

        return (
            <div className="settings_dialog_content">
                <ScrollViewer
                    HorizontalScrollBarVisibility={ScrollBarVisibility.hidden}
                    VerticalScrollBarVisibility={ScrollBarVisibility.auto}
                    style={{
                        padding: 10
                    }}>
                    <FormGroup>
                        <TextBox
                            label="Name"
                            value={form.name}
                            size={InputSize.normal}
                            spellCheck={true}
                            autoFocus={true}
                            errorMessage={errors["Name"]}
                            onTextChange={(value, originalValue) => this.onControlTextChanged(value, originalValue, "name")} />
                    </FormGroup>
                    <RulesEditor
                        mourningHalls={mourningHalls}
                        calendars={calendars}
                        isThenVisible={isThenVisible}
                        valueWhen={form.when}
                        valueThen={form.then}
                        valueCalendars={form.calendars}
                        errors={errors}
                        onWhenChanged={this.onWhenChanged}
                        onThenChanged={this.onThenChanged}
                        onCalendarAdded={this.onCalendarAdded}
                        onCalendarRemoved={this.onCalendarRemoved}
                        onCalendarsChanged={this.onCalendarChanged}
                        onThenAdded={this.onThenAdded}
                        onThenRemoved={this.onThenRemoved} />
                </ScrollViewer>
            </div>
        );
    }

    /**
     * Rendert diese Komponente.
     */
    public render(): JSX.Element
    {
        const {
            isLoading,
            mode,
            rule,
            form,
            errors,
            alertMessage,
            alertType
        } = this.state;

        var icon: string = "loading";
        var title: string = "Daten werden geladen...";
        var buttonHeader: string = "";

        if (!isLoading)
        {
            if (mode === Mode.create)
            {
                icon = "plus";
                title = "Neue Regel erstellen";
                buttonHeader = "Erstellen";
            }
            else
            {
                icon = "pencil";
                title = `Regel "${rule.name}" bearbeiten`;
                buttonHeader = "Speichern";
            }
        }

        return (
            <Window
                isOpen={true}
                titleIcon={icon}
                title={title}
                onClosing={this.onClosing}
                isMaximized={false}
                onEnterKeyUp={this.onSaveOrCreateClick}
                onEscapeKeyUp={() => this.onClosing(false)}>
                <div className="settings_dialog">
                    <Alert
                        style={utils.getGridChildStyle(1, 1, 1, 1)}
                        alertType={alertType}
                        isVisible={alertMessage ? true : false}
                        isClosable={true}
                        onHide={() => this.setState({ alertMessage: null, alertType: null })}>
                        {alertMessage}
                    </Alert>
                    {isLoading ? <Loader loaderStyle={LoaderStyle.style1} /> : this.renderContent()}
                    <div className="settings_dialog_actions">
                        {
                            mode === Mode.edit &&
                            <Button
                                text="Löschen"
                                type={ButtonType.danger}
                                onClick={this.onDeleteClick} />
                        }
                        <div style={{ flexGrow: 1 }}></div>
                        <Button
                            text={buttonHeader}
                            type={ButtonType.primary}
                            isDisabled={this._isSaveButtonDisabled}
                            onClick={this.onSaveOrCreateClick} />
                        <Button
                            text="Abbrechen"
                            type={ButtonType.default}
                            onClick={() => this.onClosing(false)} />
                    </div>
                </div>
            </Window>
        );
    }
}